//@ts-nocheck
// src/components/AddUserModal.tsx
import React, { useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Modal from '../../../components/bootstrap/Modal';
import Button from '../../../components/bootstrap/Button';
import FormGroup from '../../../components/bootstrap/forms/FormGroup';
import Input from '../../../components/bootstrap/forms/Input';
import {
  CardActions,
  CardBody,
  CardHeader,
  CardLabel,
  CardSubTitle,
  CardTitle,
} from '../../../components/bootstrap/Card';
import userState from '../../../shared/mobx/user-state';

const baseSchema = {
  firstName: Yup.string()
  .min(3, 'First Name must be at least 3 characters')
  .required('First Name is required'),

lastName: Yup.string()
  .min(3, 'Last Name must be at least 3 characters')
  .required('Last Name is required'),

  email: Yup.string().email('Invalid email format').required('Email is required'),
};

// Password validation rules

const passwordRules = {
  password: Yup.string()
    .min(3, 'Password must be between 3 to 30 characters long')
    .max(30, 'Password must be between 3 to 30 characters long')
    .required('Password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Passwords must match')
    .required('Confirm password is required'),
};

// Function to create the validation schema based on the presence of user.id
const createValidationSchema = (user = {}, mode = "default") => {
  let schemaFields = { ...baseSchema };

  if (!user.id) {
    // For new users, password fields are required
    schemaFields = { ...schemaFields, ...passwordRules };
  } else {
    // For existing users, password fields are optional, but if provided, must match
    schemaFields.password = Yup.string();
    schemaFields.confirmPassword = Yup.string()
      .when("password", {
        is: val => val && val.length > 0, // only apply if password is not empty
        then: Yup.string().oneOf([Yup.ref('password'), null], 'Passwords must match'),
      });

    if (mode === "manual") {
      schemaFields.pdf_limit = Yup.number()
        .typeError("PDF Limit must be a number")
        .min(0, "PDF Limit must be a non-negative number")
        .required("PDF Limit is required");
    }
  }

  return Yup.object(schemaFields);
};
const AddUserModal = ({ isOpen, onClose, refresher, user={}, mode="default", onSuccess }) => {
    const [isLoading, setLoading]=useState(false);
    const validationSchema = createValidationSchema(user, mode);
    const showEmailField = !(mode === "manual" && !!user.id);
    const isEdit = Boolean(user?.id);
    const modalTitle = isEdit ? 'Edit User' : 'Add New User'; 

    const initialValues = useMemo(
      () => ({
        firstName: user?.firstName ?? '',
        lastName: user?.lastName ?? '',
        email: user?.email ?? '',
        pdf_limit:
          user?.pdf_limit != null && user?.pdf_limit !== ''
            ? user.pdf_limit
            : user?.pdfLimit != null && user?.pdfLimit !== ''
              ? user.pdfLimit
              : '',
        password: '',
        confirmPassword: '',
      }),
      [
        user?.id,
        user?.firstName,
        user?.lastName,
        user?.email,
        user?.pdf_limit,
        user?.pdfLimit,
      ],
    );

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      let response = null;
        if(user.id) {
          const isSuccess = await userState.updateUser({
            ...values,
            id:user.id,
            ...(mode === "manual" ? { pdf_limit: values.pdf_limit } : {}),
          });
          response = isSuccess ? { status: true } : null;
        } else {
          response = await userState.createUser(values, mode === "manual" ? "manual" : "default");
      }
      setLoading(false);
      if (response?.status) {
        if (onSuccess) {
          await onSuccess(response, values);
        }
        refresher();
        onClose();
      }
    },
  });

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={onClose} // Assuming your Modal component can handle this prop for closing itself
      titleId={isEdit ? 'edit-user-modal' : 'add-new-user-modal'}
      isStaticBackdrop={true}
      isCentered={true}
      isAnimation={true}
      title={modalTitle}
      onClose={onClose} // Assuming your Modal component uses this prop as a callback to close the modal
    >
      <>
        <CardHeader>
          <CardLabel icon='Person'>
            <CardTitle tag='div' className='h5'>
              {modalTitle}
            </CardTitle>
            <CardSubTitle tag='div' className='h6'>
              User's Information
            </CardSubTitle>
          </CardLabel>
        </CardHeader>
        <CardBody>
          <form onSubmit={formik.handleSubmit} noValidate className='new-user-form'>
            {/* Form fields here, using Formik's handleChange and handleBlur */}
            {/* First Name, Last Name, Display Name, Email, Password, Confirm Password */}
            <div className='row g-4'>
              {/* First Name */}
              <FormGroup id='firstName' label='First Name' isFloating>
                <Input
                  name='firstName'
                  placeholder='First Name'
                  autoComplete='given-name'
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.firstName}
                />
                {formik.touched.firstName && formik.errors.firstName && <div className="text-danger">{formik.errors.firstName}</div>}
              </FormGroup>

              {/* Last Name */}
              <FormGroup id='lastName' label='Last Name' isFloating>
                <Input
                  name='lastName'
                  placeholder='Last Name'
                  autoComplete='family-name'
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.lastName}
                />
                {formik.touched.lastName && formik.errors.lastName && <div className="text-danger">{formik.errors.lastName}</div>}
              </FormGroup>


              {showEmailField && (
                <FormGroup id='email' label='Email' isFloating>
                  <Input
                    name='email'
                    type='email'
                    disabled={!!user.id}
                    placeholder='Email'
                    autoComplete='email'
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.email}
                  />
                  {formik.touched.email && formik.errors.email && <div className="text-danger">{formik.errors.email}</div>}
                </FormGroup>
              )}

              {user.id && mode === "manual" && (
                <FormGroup id='pdf_limit' label='PDF Limit' isFloating>
                  <Input
                    name='pdf_limit'
                    type='number'
                    min={0}
                    placeholder='PDF Limit'
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.pdf_limit}
                  />
                  {formik.touched.pdf_limit && formik.errors.pdf_limit && (
                    <div className="text-danger">{formik.errors.pdf_limit}</div>
                  )}
                </FormGroup>
              )}
    
                  {
                    !user.id && (<>
                    {/* Password */}
                  <FormGroup id='password' label='Password' isFloating>
                    <Input
                      name='password'
                      type='password'
                      placeholder='Password'
                      autoComplete='new-password'
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.password}
                    />
                    {formik.touched.password && formik.errors.password && <div className="text-danger">{formik.errors.password}</div>}
                  </FormGroup>
    
                  {/* Confirm Password */}
                  <FormGroup id='confirmPassword' label='Confirm Password' isFloating>
                    <Input
                      name='confirmPassword'
                      type='password'
                      placeholder='Confirm Password'
                      autoComplete='new-password'
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.confirmPassword}
                    />
                    {formik.touched.confirmPassword && formik.errors.confirmPassword && <div className="text-danger">{formik.errors.confirmPassword}</div>}
                  </FormGroup>
                    </>)
                  }
    
                  {/* Submit Button */}
                  <CardActions className='text-end'>
                  <Button 
                    
                  type="button" className='auth-btn' isDisable={isLoading}  color="dangar" onClick={()=>onClose(false)}>
                      Cancel
                    </Button>
                    <Button isDisable={isLoading} type="submit" color="primary" className='auth-btn'>
                            {  !isLoading ? ( 
                               user?.id ? ( <> Update </>) : (<>Submit</>)
                            ) : <>Please wait...</>}
                    </Button>
        
                  </CardActions>
                </div>
          </form>
        </CardBody>
         </>
        </Modal>
      );
    };
    
    export default AddUserModal;
    
